import {
  Suspense, useEffect, ElementType,
} from 'react';
import { useLocation } from 'react-router';
import Loading from 'app/layout/components/Loading';
import { ErrorBoundary, BreadcrumbNavigation } from 'app/layout/components/index';

import { useLayout } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setActiveMenu, changeLoadingState, setNoAccess } from 'app/settings/slices';
import { selectBreadCrumbParams, selectNoAccess } from 'app/settings/selectors';
import { selectUserPartnerAccessIds } from 'app/auth/selectors';
import { FullscreenProvider } from 'contexts/Fullscreen';

import {
  PartnerSettings, BrandSettings, Settings, BreadcrumbConfig, RouteHideContext,
} from 'router/types';
import { RouteLeavingContainer } from 'shared/routeLeaving';
import { NoAccess } from 'shared/emptyContent';

interface PagesRest {
  partnerSettings?: PartnerSettings;
  brandSettings?: BrandSettings;
  showConversion?: boolean;
  timeZoneSettings?: Settings;
}

export interface PagesProps {
  component: ElementType;
  breadcrumb?: BreadcrumbConfig[];
  rest?: PagesRest
  activeMenu: number,
  hide?: (args: RouteHideContext) => boolean,
  isNewVersion?: boolean,
  partnerAccessIds?: number[],
}

const Pages = ({
  component: Component, breadcrumb = [], rest = {}, activeMenu, hide, isNewVersion = false,
}: PagesProps) => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const breadcrumbParams = useAppSelector(selectBreadCrumbParams);
  const partnerAccessIds = useAppSelector(selectUserPartnerAccessIds) ?? [];
  const noAccess = useAppSelector(selectNoAccess);

  const {
    partnerSettings, brandSettings, showConversion = false, timeZoneSettings,
  } = rest;

  const {
    setPartnerSettings, setBrandSettings, setShowConversion, setTimeZoneSettings,
  } = useLayout();

  useEffect(() => {
    setPartnerSettings?.(partnerSettings);
  }, [setPartnerSettings, partnerSettings]);

  useEffect(() => {
    setBrandSettings?.(brandSettings);
  }, [setBrandSettings, brandSettings]);

  useEffect(() => {
    setShowConversion?.(showConversion);
  }, [showConversion, setShowConversion]);

  useEffect(() => {
    setTimeZoneSettings?.(timeZoneSettings);
  }, [timeZoneSettings, setTimeZoneSettings]);

  useEffect(() => {
    if (activeMenu) {
      dispatch(setActiveMenu(activeMenu));
    }
  }, [activeMenu, dispatch]);

  useEffect(() => {
    dispatch(changeLoadingState(false));
  }, [dispatch, location.pathname]);

  const getBreadcrumb = () => breadcrumb.map((bc) => {
    if (bc.key) {
      return {
        ...bc,
        name: breadcrumbParams[bc.key],
      };
    }
    return bc;
  });

  useEffect(() => {
    dispatch(setNoAccess(false));
  }, [location.key, dispatch]);

  if (noAccess) {
    return <NoAccess />;
  }

  const page = (
    <ErrorBoundary locationKey={location.key}>
      {!!breadcrumb?.length && <BreadcrumbNavigation breadcrumb={getBreadcrumb()} />}
      <Suspense fallback={<span />}>
        <RouteLeavingContainer />
        <FullscreenProvider>
          <Loading />
          <Component {...rest} />
        </FullscreenProvider>
      </Suspense>
    </ErrorBoundary>
  );

  // todo temp solution to remove in further
  const isHidden = hide?.({ isNewVersion, partnerAccessIds });
  if (isHidden && brandSettings?.isNewVersion) {
    return '';
  }

  return isHidden ? <NoAccess /> : page;
};

export default Pages;
